import { Entry, ResponseFields } from "@dreambigger/shared/src/types";
import { ADDRESS_SUBKEY_SUFFIX_SET } from "../steps/custom-collections/utils/addressSubkeys";

/** Inverse of `entriesToResponseFields`. Keys that don't match the
 *  `<fieldKey>__<index>` suffix pattern (or the address-subkey variant
 *  `<fieldKey>__<index>__<suffix>`) are ignored. */
export default function entriesFromResponseFields(
  fields: ResponseFields | undefined
): Entry[] {
  if (!fields || Object.keys(fields).length === 0) return [];
  const addressMatch = /^(.+)__(\d+)__([^_]+)$/;
  const trailingMatch = /^(.+)__(\d+)$/;
  const indexed = new Map<number, Entry>();
  const ensure = (index: number): Entry => {
    let entry = indexed.get(index);
    if (!entry) {
      entry = { id: index };
      indexed.set(index, entry);
    }
    return entry;
  };
  for (const [key, value] of Object.entries(fields)) {
    const addr = addressMatch.exec(key);
    if (addr) {
      if (ADDRESS_SUBKEY_SUFFIX_SET.has(addr[3])) {
        const [, base, indexStr, suffix] = addr;
        ensure(Number(indexStr))[`${base}__${suffix}`] = value as Entry[string];
        continue;
      }
      // Composite-looking key with a non-address suffix. Only `address` uses
      // the `<base>__<index>__<suffix>` serialization today, so this would
      // mean a new composite fieldType was added without updating this
      // allowlist. Log in dev and fall through to the generic index match so
      // the top-level value still round-trips.
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `[entriesFromResponseFields] unhandled composite suffix "${addr[3]}" in key "${key}" — add it to ADDRESS_SUBKEY_SUFFIXES in steps/custom-collections/utils/addressSubkeys.ts or extend composite handling`
        );
      }
    }
    const match = trailingMatch.exec(key);
    if (!match) continue;
    const [, fieldKey, indexStr] = match;
    ensure(Number(indexStr))[fieldKey] = value as Entry[string];
  }
  return Array.from(indexed.entries())
    .sort(([a], [b]) => a - b)
    .map(([, entry]) => entry);
}
